/**
 * UseCase identifiers mirroring the backend `UseCase` enum
 * (`src/metadata/use_cases.py`). Values are the wire-format strings sent over
 * the API so an enum member compares equal to its backend counterpart and
 * deserialized JSON payloads are assignable to a `UseCase` field with no
 * conversion at the type boundary.
 *
 * Use the enum members (e.g. `UseCase.Monadic`) — not raw string literals —
 * to reference a use case anywhere in the app, so a typo surfaces at compile
 * time instead of silently bypassing a `switch`.
 */
export enum UseCase {
  PreferenceTest = "preference_test",
  AdCreative = "ad_creative",
  Awareness = "awareness",
  ClaimTest = "claim_test",
  MaxDiff = "max_diff",
  Monadic = "monadic",
  NPS = "nps",
  Packaging = "packaging",
  PriceElasticity = "price_elasticity",
  TURF = "turf",
}

/**
 * Convert a backend `UseCase` value (snake_case, e.g. "preference_test") into
 * the URL slug used by the quick-insights routes (kebab-case, e.g.
 * "preference-test").
 *
 * The two formats diverge intentionally: the wire format follows Python /
 * database conventions, while the URL format follows web conventions and
 * matches the static slugs used in `quick-insights/[useCase]/_layout.tsx`
 * and the picker grid in `quick-insights/index.tsx`.
 */
export const getUseCaseSlug = (useCase: UseCase): string =>
  useCase.replace(/_/g, "-");

/**
 * Inverse of `getUseCaseSlug` — parse the URL slug (kebab-case) back into
 * the `UseCase` enum value (snake_case). Returns `null` if the slug doesn't
 * match a known use case so the caller can render a 404-style fallback.
 */
export const parseUseCaseSlug = (slug: string): UseCase | null => {
  const value = slug.replace(/-/g, "_");
  return (Object.values(UseCase) as string[]).includes(value)
    ? (value as UseCase)
    : null;
};
